import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FeedbackIcon from "@mui/icons-material/FeedbackOutlined";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import LogoutIcon from "@mui/icons-material/Logout";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import WorkIcon from "@mui/icons-material/Work";
import { Divider, List, ListItemButton, ListItemIcon, ListItemText, type SxProps } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { VIDEO_LIBRARY_PATH } from "@/pages/VideoLibrary/consts";
import { INK } from "@/theme/tokens";
import { CAMPAIGN_FEEDBACK_FORM_URL, DEFAULT_FEEDBACK_FORM_URL, DepartmentNames } from "@/utils/models";
import { ViewSwitchMenuItem } from "./ViewSwitchMenuItem";

// Adds the secondary "current view" line, styled down so it reads as context.
const switchTextSx = {
	"& .MuiListItemText-primary": { color: INK.SECONDARY, fontWeight: 500 },
	"& .MuiListItemText-secondary": { color: INK.SECONDARY, opacity: 0.7, fontSize: "13px" },
} as const satisfies SxProps;

type Props = {
	handleDrawerToggle: () => void;
	handleLogout: () => void;
	navigate: (path: string) => void;
	headerStyles?: any;
};

const MobileDrawerOptions: FC<Props> = ({ navigate, handleLogout, handleDrawerToggle, headerStyles }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo, isSnfDemo, isMedicalDepartment } = useDepartmentFlags();
	const selfSchedulingEnabled = useFeatureFlag(FEATURE_FLAGS.SelfSchedulingEnabled);
	// The InHouse campaign facility gets its own feedback form; every other dept keeps the default.
	const feedbackFormUrl =
		chosenDepartment?.department_id === DepartmentNames.BrightwaterSunriseMeadows ? CAMPAIGN_FEEDBACK_FORM_URL : DEFAULT_FEEDBACK_FORM_URL;

	return (
		<List data-testid="mobile-drawer-nav">
			<ListItemButton
				data-testid="bottom-nav-schedule"
				onClick={() => {
					handleDrawerToggle();
					navigate("/my-schedule");
				}}
			>
				<ListItemIcon sx={headerStyles.listIcon}>
					<CalendarMonthIcon />
				</ListItemIcon>
				<ListItemText primary={t("schedule")} sx={headerStyles.listItemText} />
			</ListItemButton>
			{!isMedicalDepartment && (
				<ListItemButton
					data-testid="bottom-nav-open-shifts"
					onClick={() => {
						handleDrawerToggle();
						navigate("/open-shifts");
					}}
				>
					<ListItemIcon sx={headerStyles.listIcon}>
						<WorkIcon />
					</ListItemIcon>
					<ListItemText primary={t("open.shifts")} sx={headerStyles.listItemText} />
				</ListItemButton>
			)}
			{!isMedicalDepartment && (
				<ListItemButton
					data-testid="bottom-nav-preferences"
					onClick={() => {
						handleDrawerToggle();
						const path = `/${chosenDepartment.department_id}/preferences`;
						navigate(path);
					}}
				>
					<ListItemIcon sx={headerStyles.listIcon}>
						<ThumbsUpDownIcon />
					</ListItemIcon>
					<ListItemText primary={t("work.preferences")} sx={headerStyles.listItemText} />
				</ListItemButton>
			)}
			{(isHospitalDemo || isSnfDemo || selfSchedulingEnabled) && (
				<ListItemButton
					data-testid="bottom-nav-self-scheduling"
					onClick={() => {
						handleDrawerToggle();
						navigate("/self-scheduling");
					}}
				>
					<ListItemIcon sx={headerStyles.listIcon}>
						<InsertInvitationIcon />
					</ListItemIcon>
					<ListItemText primary={t("self.scheduling")} sx={headerStyles.listItemText} />
				</ListItemButton>
			)}
			<ListItemButton
				data-testid="bottom-nav-feedback"
				LinkComponent={"a"}
				href={feedbackFormUrl}
				target={"_blank"}
				rel={"noopener noreferrer"}
				sx={{ borderRadius: 1 }}
			>
				<ListItemIcon sx={{ minWidth: 40 }}>
					<FeedbackIcon />
				</ListItemIcon>
				<ListItemText
					primary={t("share.feedback")}
					sx={{
						"& .MuiListItemText-primary": { color: INK.SECONDARY },
					}}
				/>
			</ListItemButton>
			{chosenDepartment.can_view_nurses && (
				<ListItemButton
					data-testid="bottom-nav-day-schedule"
					onClick={() => {
						handleDrawerToggle();
						const path = `/${chosenDepartment.department_id}/day-schedule/${DateTime.now().toFormat("yyyy-MM-dd")}`;
						navigate(path);
					}}
				>
					<ListItemIcon sx={headerStyles.listIcon}>
						<InsertInvitationIcon />
					</ListItemIcon>
					<ListItemText primary={t("day.schedule")} sx={headerStyles.listItemText} />
				</ListItemButton>
			)}
			<ListItemButton
				data-testid="bottom-nav-video-library"
				onClick={() => {
					handleDrawerToggle();
					navigate(VIDEO_LIBRARY_PATH);
				}}
			>
				<ListItemIcon sx={headerStyles.listIcon}>
					<OndemandVideoOutlinedIcon />
				</ListItemIcon>
				<ListItemText primary={t("video_library.viewer.menu")} sx={headerStyles.listItemText} />
			</ListItemButton>
			<Divider sx={{ my: 1 }} />
			{/* Dual-role users only; shared with AdminDrawer via ViewSwitchMenuItem. */}
			<ViewSwitchMenuItem
				dataTestId="bottom-nav-switch-view"
				onBeforeSwitch={handleDrawerToggle}
				iconContainerSx={headerStyles.listIcon}
				textSx={switchTextSx}
				showCurrentView
			/>
			<ListItemButton
				data-testid="bottom-nav-settings"
				onClick={() => {
					handleDrawerToggle();
					const path = chosenDepartment?.department_id ? `/${chosenDepartment.department_id}/settings` : "/settings";
					navigate(path);
				}}
			>
				<ListItemIcon sx={headerStyles.listIcon}>
					<SettingsIcon />
				</ListItemIcon>
				<ListItemText primary={t("Settings")} sx={headerStyles.listItemText} />
			</ListItemButton>
			<ListItemButton
				data-testid="bottom-nav-logout"
				onClick={() => {
					handleDrawerToggle();
					handleLogout();
				}}
			>
				<ListItemIcon sx={headerStyles.listIcon}>
					<LogoutIcon />
				</ListItemIcon>

				<ListItemText primary={t("Logout")} sx={headerStyles.listItemText} />
			</ListItemButton>
		</List>
	);
};

export default MobileDrawerOptions;
